import React, { useCallback, useContext, useEffect, useState } from "react";
import * as notify from "shared/notify";
import Loading from "components/callback/loading";
import eventEmitter from "shared/events";
import { useQueryClient } from "react-query";
import { attributeFeedbackQueryKey } from "components/attributes/attribute-feedback/lib/constants";

import {
    getWip,
    getAttributeValuesByTaskKey,
    sendSingleAttributeData,
    sendMultipleAttributesData,
    getDefaultValueByType,
    mapAttribute,
    parseDefaultValue,
    deleteAttribute,
} from "../../components/attributes/lib/attribute-data";
import { TaskContext } from "contexts/task";
import { AttributesProvider } from "./index";
import { useAttributeFactory } from "shared/hooks/use-attribute-factory";

const isComponentSaved = (type) => {
    switch (type) {
        case "DrugPanel":
        case "EnergyPanel":
        case "GuaranteedAnalysisPanel":
        case "LightingPanel":
        case "NutritionPanel":
        case "SupplementPanel":
            return true;
        default:
            return false;
    }
};

const Attributes = (props) => {
    const [refreshAttributes, setRefreshAttributes] = useState(false);
    const currentTask = useContext(TaskContext);
    const queryClient = useQueryClient();
    const { task } = currentTask ? currentTask : props.useTask;
    const codingWipKey = task.context.codingWipKey;
    const codingWipKeyArray = task.context.codingWipKeys || null;
    const taskKey = task.key;
    const [isLoaded, setIsLoaded] = useState(false);
    const [data, setData] = useState(null);
    const remapAttributeValues = useAttributeFactory();

    useEffect(() => {
        eventEmitter.on("REFRESH_ATTRIBUTES", refresh);

        return () => eventEmitter.removeListener("REFRESH_ATTRIBUTES", refresh);
    }, []);

    useEffect(() => {
        if (refreshAttributes) {
            setRefreshAttributes(false);
        }
    }, [refreshAttributes]);

    // TODO: This effect has to be reimplemented
    useEffect(() => {
        if (props.defaultValues) {
            setData(props.defaultValues);
            setIsLoaded(true);
        } else {
            fetchAttributes();
        }

        eventEmitter.on("REFRESH_ATTRIBUTES", refresh);

        return () => {
            eventEmitter.removeListener("REFRESH_ATTRIBUTES", refresh);
        };
    }, [props.defaultValues, codingWipKey]);

    const fetchAttributes = async () => {
        if (task.sourcePage === "brandverifiedqa") {
            setData({});
            setIsLoaded(true);
            return;
        }

        if (props.coderRoles) {
            const response = await getAttributeValuesByTaskKey(codingWipKey, taskKey, true);
            setData(response);
            setIsLoaded(true);
        } else {
            const response = await getWip(codingWipKey, true);
            setData(response.attributes);
            setIsLoaded(true);
        }
    };

    const handleTriggeredChanges = (triggeredChanges, values) => {
        const added = triggeredChanges && triggeredChanges.added ? triggeredChanges.added : {};
        const updated = triggeredChanges && triggeredChanges.updated ? triggeredChanges.updated : {};
        values = {
            ...values,
            ...updated,
            ...added,
        };

        if (triggeredChanges && triggeredChanges.removed) {
            triggeredChanges.removed.forEach((uuid) => {
                delete values[uuid];
            });
        }

        return values;
    };

    const update = async (attr, value, forceSave) => {
        var newData = {
            ...data,
            [attr.uuid]: value,
        };

        if ((codingWipKey || codingWipKeyArray) && (forceSave || !isComponentSaved(attr.componentType))) {
            const attributeValues = remapAttributeValues(attr.key, value);
            let response;

            if (codingWipKeyArray && codingWipKeyArray.length > 0) {
                response = await sendMultipleAttributesData(codingWipKeyArray, taskKey, {
                    definition: attr.uuid,
                    value: attributeValues,
                    type: attr.componentType,
                });
            } else {
                response = await sendSingleAttributeData(codingWipKey, taskKey, task.sourcePage, {
                    definition: attr.uuid,
                    value: attributeValues,
                    type: attr.componentType,
                });
            }

            if (!response) {
                return;
            }

            notify.success("Saved!");

            if (response && response instanceof Array && response.length > 0) {
                if (response[0].refresh || Object.keys(response[0].triggeredChanges.added).length > 1) {
                    fetchAttributes();
                    setRefreshAttributes(true);
                    eventEmitter.emit("REFRESH_ATTRIBUTE_DEFINITIONS");

                    return;
                }

                return eventEmitter.emit("REFRESH_ATTRIBUTES");
            } else {
                if (response && response.forcePageRefresh) {
                    eventEmitter.emit("REFRESH_PAGE");
                }

                if (response && (response.refresh || Object.keys(response.triggeredChanges.added).length > 1)) {
                    fetchAttributes();
                    setRefreshAttributes(true);
                    eventEmitter.emit("REFRESH_ATTRIBUTE_DEFINITIONS");

                    return;
                }
            }

            if (response instanceof Array) {
                let changes = [];
                response.map((r) => changes.push(r.triggeredChanges));
                newData = handleTriggeredChanges(changes, newData);
            } else {
                newData = handleTriggeredChanges(response.triggeredChanges, newData);
            }
        }

        setData(newData);
    };

    const updateMany = async (values) => {
        setData({
            ...data,
            ...values,
        });
    };

    const cancelUpdate = async (attr, value) => {
        update(attr, value);
    };

    const add = async (attr, value, forceSave, isAttrSearch, isSuccess, taskType, isBrandVerified) => {
        if (!attr || !attr.uuid) {
            return;
        }

        const existing = _.has(data, attr.uuid);

        if (existing) {
            props.taskType === "scheduling.default"
                ? notify.profilingScrollToAttribute(`Attribute already added.<br />`, attr, "warning")
                : notify.scrollToAttribute(`Attribute already added.<br />`, attr, "warning", taskType);

            return;
        }

        if (!existing && isBrandVerified) {
            return notify.error("Cannot add attributes to Brand Verified product");
        }

        // Convert defaultValue to proper type
        const useValue = value
            ? value
            : attr.defaultValue
              ? parseDefaultValue(attr.defaultValue, attr.componentType)
              : getDefaultValueByType(attr.componentType);

        if (!forceSave && isComponentSaved(attr.componentType)) {
            setData({
                ...data,
                [attr.uuid]: useValue,
            });

            return;
        }

        let response;

        if (codingWipKeyArray && codingWipKeyArray.length > 0) {
            response = await sendMultipleAttributesData(codingWipKeyArray, taskKey, {
                definition: attr.uuid,
                value: useValue,
                type: attr.componentType,
            });
            response = response[0];
        } else {
            response = await sendSingleAttributeData(codingWipKey, taskKey, task.sourcePage, {
                definition: attr.uuid,
                value: useValue,
                type: attr.componentType,
            });
        }

        if (!response) {
            return;
        }

        eventEmitter.emit("ADD_ATTRIBUTE", mapAttribute(attr));

        if (response && response.success) {
            await fetchAttributes();
            setRefreshAttributes(true);
            eventEmitter.emit("REFRESH_ATTRIBUTE_DEFINITIONS");
        } else {
            setData({
                ...data,
                [attr.uuid]: useValue,
            });
        }

        if (isAttrSearch && isSuccess) {
            props.taskType === "scheduling.default"
                ? notify.profilingScrollToAttribute("Attribute Added!", attr, "success")
                : notify.scrollToAttribute("Attribute Added!", attr, "success", taskType);
        }

        if (!isAttrSearch) {
            notify.success("Attribute Added!");
        }
    };
    const clearAttributeFeedback = (uuids) => {
        const feedbackKey = [attributeFeedbackQueryKey, taskKey];

        queryClient.setQueryData(feedbackKey, (feedback) =>
            Array.isArray(feedback) ? feedback.filter((item) => !uuids.includes(item.attributeUuid)) : feedback
        );
        queryClient.invalidateQueries(feedbackKey);
    };
    const remove = async (uuid, onPerformRefresh) => {
        let response;

        if (codingWipKeyArray && codingWipKeyArray.length > 0) {
            codingWipKeyArray.forEach(async (wipKey) => {
                response = await deleteAttribute(wipKey, taskKey, uuid, task.sourcePage);
            });
            response = response[0];
        } else {
            response = await deleteAttribute(codingWipKey, taskKey, uuid, task.sourcePage);
        }

        if (!response) {
            return;
        }

        const serverRemoved = response && response.removed ? response.removed : null;
        const newAttributes = handleTriggeredChanges(response.triggeredChanges, data);

        delete newAttributes[uuid];

        if (serverRemoved) {
            serverRemoved.forEach((u) => {
                delete newAttributes[u];
            });
        }

        // The backend removes the feedback record along with the attribute - drop the cached copy too,
        // so the feedback icon does not outlive the attribute it belongs to.
        clearAttributeFeedback([uuid, ...(serverRemoved || [])]);

        notify.success("Attribute Deleted");

        if (response && response.forcePageRefresh) {
            eventEmitter.emit("REFRESH_PAGE");
        }

        if (response && task && task.taskType === "codingqa.standard") {
            eventEmitter.emit("REFRESH_ATTRIBUTE_VALUES");

            return refresh();
        }

        if (response && (response.refresh || (response.triggeredChanges && Object.keys(response.triggeredChanges.added).length > 1))) {
            fetchAttributes();
            eventEmitter.emit("REFRESH_ATTRIBUTE_DEFINITIONS");

            onPerformRefresh && onPerformRefresh();

            return;
        }

        setData(newAttributes);
    };

    const handleForceRefreshAttributes = useCallback(() => {
        setRefreshAttributes(true);

        return refresh();
    }, []);

    const refresh = async () => {
        return fetchAttributes();
    };

    const reset = (defaultValues) => {
        setData({
            ...defaultValues,
        });
    };

    if (!isLoaded) {
        return <Loading />;
    }

    return (
        <AttributesProvider
            value={{
                data,
                add,
                remove,
                update,
                updateMany,
                refresh,
                reset,
                cancelUpdate,
                refreshAttributes,
                forceRefreshAttributes: handleForceRefreshAttributes,
            }}
        >
            {props.children}
        </AttributesProvider>
    );
};

export default Attributes;
